/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/
import {
  AccessToken,
  HttpStatusCode,
  TranslatedString,
  createRFC8959AccessTokenEncoded,
  encodeCrock,
  getRandomBytes,
} from "@gnu-taler/taler-util";
import {
  useAsyncAction,
  useChallengerApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useState } from "preact/hooks";
import { safeToURL } from "../Routing.js";
import {
  ActionButton,
  formatErrorDetails,
  formatHttpErrorDetails,
  InlineNotice,
  TechnicalDetails,
  VerificationCard,
} from "../components/VerificationUi.js";
import { doAutoFocus, undefinedIfEmpty } from "./AnswerChallenge.js";

type Props = {
  clientId: string;
  secret: string | undefined;
  redirectURL: URL | undefined;
  focus?: boolean;
};

export function Setup({ clientId, secret, redirectURL, focus }: Props): VNode {
  const { i18n } = useTranslationContext();
  const { lib } = useChallengerApiContext();
  const [password, setPassword] = useState(secret ?? "");
  const [url, setUrl] = useState(redirectURL?.href ?? "");
  const [actionError, setActionError] = useState<TranslatedString>();
  const [actionErrorDetails, setActionErrorDetails] = useState<string>();
  // Translators: OAuth client credential used by a developer; this is not the
  // end user's password.
  const clientPasswordLabel = i18n.str`Client password`;
  // Translators: OAuth redirect URL configured by a developer.
  const redirectUrlLabel = i18n.str`Redirect URL`;
  // Translators: Short form-validation message shown below an empty required
  // developer-setup field.
  const requiredMessage = i18n.str`Required`;
  // Translators: Validation message for the OAuth redirect-URL field on the
  // developer session-setup screen.
  const invalidUrlMessage = i18n.str`Enter a valid HTTP or HTTPS URL`;
  // Translators: Authentication error on the developer session-setup screen;
  // “client” means an OAuth client application.
  const unrecognizedClientMessage = i18n.str`The client ID or password is not recognized by this service.`;
  // Translators: Generic server-rejection error while creating a developer
  // verification session.
  const setupFailureMessage = i18n.str`The verification session could not be created.`;
  // Translators: Network-error message on the developer session-setup screen.
  const connectionFailureMessage = i18n.str`We could not contact the verification service. Check the service URL and try again.`;
  // Translators: Page title for the developer form that manually creates a
  // Challenger verification session.
  const setupTitle = i18n.str`Create a verification session`;
  // Translators: Error-notice heading when the developer form fails to create a
  // verification session.
  const sessionNotCreatedTitle = i18n.str`Session not created`;
  // Translators: Busy-state label on the developer form's session-creation
  // button.
  const creatingSessionLabel = i18n.str`Creating session…`;
  // Translators: Button on the developer setup screen that submits the form and
  // creates a new verification session.
  const createSessionLabel = i18n.str`Create session`;
  const errors = undefinedIfEmpty({
    password: !password ? requiredMessage : undefined,
    url: !url
      ? requiredMessage
      : !safeToURL(url)
        ? invalidUrlMessage
        : undefined,
  });
  const startArgs =
    errors || !password || !url
      ? undefined
      : ([createRFC8959AccessTokenEncoded(password), url] as const);

  const start = useAsyncAction<
    Awaited<ReturnType<typeof lib.challenger.setup>>,
    [AccessToken, string]
  >((ct, token) => lib.challenger.setup(clientId, token), {
    onResult(result, _token, redirectUri) {
      if (result.type === "fail") {
        setActionError(
          result.case === HttpStatusCode.NotFound
            ? unrecognizedClientMessage
            : setupFailureMessage,
        );
        setActionErrorDetails(formatHttpErrorDetails(result));
        return;
      }
      const redirect = new URL(window.location.href);
      redirect.searchParams.set("client_id", clientId);
      redirect.searchParams.set("redirect_uri", redirectUri);
      redirect.searchParams.set("state", encodeCrock(getRandomBytes(32)));
      redirect.searchParams.set("nonce", result.body.nonce);
      redirect.hash = "/ask";
      window.location.href = redirect.href;
    },
    onError(error) {
      setActionError(connectionFailureMessage);
      setActionErrorDetails(formatErrorDetails(error));
    },
  });

  return (
    <VerificationCard
      title={setupTitle}
      description={
        // Translators: This screen is only for developers. %1$s is an OAuth
        // client identifier configured in Challenger.
        <i18n.Translate>Developer setup for client {clientId}</i18n.Translate>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (startArgs) void start.run(...startArgs);
        }}
      >
        <div>
          <label for="password" class="block text-sm font-semibold">
            {clientPasswordLabel}
          </label>
          <input
            ref={focus ? doAutoFocus : undefined}
            id="password"
            name="password"
            type="password"
            maxLength={512}
            autoComplete="current-password"
            value={password}
            readOnly={secret !== undefined}
            aria-invalid={!!errors?.password}
            class="mt-2 block w-full rounded-lg border px-3.5 py-2.5 shadow-sm focus:ring-2 read-only:opacity-70"
            onInput={(event) => setPassword(event.currentTarget.value)}
          />
          {errors?.password ? (
            <p class="mt-1 text-sm text-error dark:text-darkError">
              {errors.password}
            </p>
          ) : undefined}
        </div>

        <div class="mt-5">
          <label for="redirect_url" class="block text-sm font-semibold">
            {redirectUrlLabel}
          </label>
          <input
            id="redirect_url"
            name="redirect_url"
            type="url"
            maxLength={512}
            autoComplete="url"
            value={url}
            readOnly={redirectURL !== undefined}
            aria-invalid={!!errors?.url}
            class="mt-2 block w-full rounded-lg border px-3.5 py-2.5 shadow-sm focus:ring-2 read-only:opacity-70"
            onInput={(event) => setUrl(event.currentTarget.value)}
          />
          {errors?.url ? (
            <p class="mt-1 text-sm text-error dark:text-darkError">
              {errors.url}
            </p>
          ) : undefined}
        </div>

        {actionError ? (
          <div class="mt-5">
            <InlineNotice tone="error" title={sessionNotCreatedTitle}>
              {actionError}
            </InlineNotice>
            {actionErrorDetails ? (
              <TechnicalDetails copyText={actionErrorDetails}>
                {actionErrorDetails}
              </TechnicalDetails>
            ) : undefined}
          </div>
        ) : undefined}

        <div class="mt-6">
          <ActionButton
            submit
            busyLabel={creatingSessionLabel}
            running={start.running}
            disabled={!startArgs}
          >
            {createSessionLabel}
          </ActionButton>
        </div>
      </form>
    </VerificationCard>
  );
}
